import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Close, Download, Info, Save } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { TextButton } from "../../../components/commonComponents/Buttons/ActionButton";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import {
  ListingCellCenter,
  ListingCellLeft,
  ListingHeaderContent,
} from "../../../components/commonComponents/TabularLists/styles";
import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import {
  HtmlTooltip,
  IconContainer,
  StyledCheckbox,
  StyledDarkRedInformationTypography,
  StyledWhiteCheckbox,
} from "../../../components/commonComponents/StyledComponents/styled";
import { allWeekDayOptions, checkTodayDateWithFormat } from "../../../api_calls/utils";
import { getStoresDetailedAvailableHours } from "../../../api_calls/campaignRequests";
import * as xlsx from "xlsx";

// Store Availability filter options for the table
const ALL_AVAILABILITY_OPTION = { label: "All", value: "all" };
const STORE_AVAILABILITY_OPTIONS = [
  ALL_AVAILABILITY_OPTION,
  { label: "Only All Available", value: "all_available" },
  { label: "Only Partially Available", value: "partially_available" },
];

const AVAILABILITY_TONES = {
  all: "#AF3241",
  some: "#F04B55",
  none: "#62626E",
};

const WEEK_COUNT_COL_SX = {
  whiteSpace: "nowrap",
};

const resolveCell = (rowCells, key, storeTotal) => {
  const cell = rowCells?.[key];
  if (cell && typeof cell.count === "number") return cell;
  return { count: 0, total: storeTotal };
};

const availabilityTone = (count, total) => {
  if (!total || count <= 0) return "none";
  if (count >= total) return "all";
  return "some";
};

const StoreAvailabilityLegend = () => (
  <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
    {[
      { tone: "all", label: "All selected stores available" },
      { tone: "some", label: "Some selected stores available" },
      { tone: "none", label: "None of selected stores available" },
    ].map((item) => (
      <Stack key={item.tone} direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: AVAILABILITY_TONES[item.tone],
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: "#62626E" }}>
          {item.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

// Parses detailedOpenedHours — UPPERCASE day keys → TitleCase, total = storeIds.length
const toTitleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const parseHourMatrix = (responseBody, storeTotal) => {
  const matrix = {};
  if (!responseBody || typeof responseBody !== "object") return matrix;

  const dhMap = responseBody.detailedOpenedHours;
  if (!dhMap || typeof dhMap !== "object") return matrix;

  for (const hourKey of Object.keys(dhMap)) {
    const hourInt = Number(hourKey);
    if (isNaN(hourInt)) continue;
    const daysObj = dhMap[hourKey];
    if (!daysObj || typeof daysObj !== "object") continue;
    if (!matrix[hourInt]) matrix[hourInt] = {};
    for (const dayKey of Object.keys(daysObj)) {
      const dayValue = toTitleCase(dayKey);
      const count = daysObj[dayKey];
      if (typeof count !== "number") continue;
      matrix[hourInt][dayValue] = { count, total: storeTotal };
    }
  }

  return matrix;
};

const DetailedHourSelection = ({ open, onClose, usedFor, availableHours = [] }) => {
  const dispatch = useDispatch();
  const currentStateData = useSelector((state) => state.userSelections);

  // Local selection state — only written to Redux on Apply
  const [localSelectedHours, setLocalSelectedHours] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState(ALL_AVAILABILITY_OPTION);

  const [hourMatrix, setHourMatrix] = useState({});

  // -------- Sync: initialize local selection from Redux on popup open --------
  useEffect(() => {
    if (!open) return;

    const currentHours = currentStateData.new_clip_data?.hours || [];
    setLocalSelectedHours(currentHours);
    setAvailabilityFilter(ALL_AVAILABILITY_OPTION);
    setHourMatrix({});
  }, [open]);

  // -------- Fetch: detailed hour availability --------
  useEffect(() => {
    if (!open) return;

    const storeIds = (currentStateData.selected_store_options || [])
      .map((s) => s.id)
      .filter((id) => id !== "all" && typeof id !== "undefined");
    const startDate = currentStateData.new_clip_data?.clip_start_date;
    const endDate = currentStateData.new_clip_data?.clip_end_date;
    const calenderWeeks = currentStateData.new_clip_data?.clip_weeks || [];
    const selectedDays = currentStateData.new_clip_data?.clip_week_days || [];

    if (storeIds.length === 0 || !startDate || !endDate || selectedDays.length === 0) {
      setHourMatrix({});
      return;
    }

    let cancelled = false;
    getStoresDetailedAvailableHours({
      storeIds,
      startDate,
      endDate,
      calenderWeeks,
      weekdays: selectedDays,
    }).then((result) => {
      if (cancelled) return;
      if (result.success === true) {
        setHourMatrix(parseHourMatrix(result.data, storeIds.length));
      } else {
        setHourMatrix({});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    currentStateData.selected_store_options,
    currentStateData.new_clip_data?.clip_start_date,
    currentStateData.new_clip_data?.clip_end_date,
    currentStateData.new_clip_data?.clip_weeks,
    currentStateData.new_clip_data?.clip_week_days,
  ]);

  // -------- Selected weekday columns (Mon-Sun order, only selected days) --------
  const sortedWeekdayColumns = useMemo(() => {
    const selectedDayOptions = currentStateData.new_clip_data?.clip_week_day_options || [];

    // Filter allWeekDayOptions to only include selected ones — preserves Mon→Sun order
    return allWeekDayOptions.filter((opt) =>
      selectedDayOptions.some((sel) => sel.value === opt.value)
    );
  }, [currentStateData.new_clip_data?.clip_week_day_options]);

  const storeTotal = (currentStateData.selected_store_options || []).filter(
    (s) => s.id !== "all"
  ).length;

  const isRowUnavailable = (row) => {
    if (Object.keys(hourMatrix).length === 0 || sortedWeekdayColumns.length === 0) return false;
    return sortedWeekdayColumns.every(
      (day) => resolveCell(hourMatrix[row.value], day.value, storeTotal).count === 0
    );
  };

  // -------- Filter rows by Store Availability --------
  const filteredRows = useMemo(() => {
    if (!availabilityFilter || availabilityFilter.value === "all") return availableHours;

    return availableHours.filter((row) => {
      const allFull =
        sortedWeekdayColumns.length > 0 &&
        sortedWeekdayColumns.every((day) => {
          const cell = resolveCell(hourMatrix[row.value], day.value, storeTotal);
          return cell.total > 0 && cell.count === cell.total;
        });
      return availabilityFilter.value === "all_available" ? allFull : !allFull;
    });
  }, [availableHours, availabilityFilter, hourMatrix, sortedWeekdayColumns, storeTotal]);

  // -------- Selection helpers --------
  const isHourSelected = (hourValue) => localSelectedHours.includes(hourValue);

  const toggleHour = (hourValue, row) => {
    if (row && isRowUnavailable(row)) return;
    setLocalSelectedHours((prev) =>
      isHourSelected(hourValue)
        ? prev.filter((h) => h !== hourValue)
        : [...prev, hourValue]
    );
  };

  const selectableRows = filteredRows.filter((row) => !isRowUnavailable(row));

  const areAllVisible = () =>
    selectableRows.length > 0 &&
    selectableRows.every((row) => isHourSelected(row.value));

  const toggleSelectAll = (checked) => {
    if (checked) {
      setLocalSelectedHours((prev) => {
        const existing = new Set(prev);
        const toAdd = selectableRows
          .filter((row) => !existing.has(row.value))
          .map((row) => row.value);
        return [...prev, ...toAdd];
      });
    } else {
      const visibleValues = new Set(selectableRows.map((row) => row.value));
      setLocalSelectedHours((prev) => prev.filter((h) => !visibleValues.has(h)));
    }
  };

  // -------- Apply: write local selection to Redux --------
  const handleApply = () => {
    dispatch(
      updateStateData({
        new_clip_data: {
          ...currentStateData.new_clip_data,
          hours: localSelectedHours.filter((h) => {
            const row = availableHours.find((opt) => opt.value === h);
            return row ? !isRowUnavailable(row) : false;
          }),
        },
      })
    );
    onClose();
  };

  // -------- Excel export (selected rows only) --------
  const downloadExcelFile = () => {
    const headers = [
      "Hour of Day",
      ...sortedWeekdayColumns.map((d) => d.label),
    ];

    const rows = localSelectedHours.map((hourValue) => {
      const hourOption = availableHours.find((h) => h.value === hourValue);
      const hourLabel = hourOption?.label ?? `${String(hourValue).padStart(2, "0")}:00 - ${String(hourValue).padStart(2, "0")}:59`;

      const cellValues = sortedWeekdayColumns.map((day) => {
        const cell = resolveCell(hourMatrix[hourValue], day.value, storeTotal);
        return `${cell.count}/${cell.total}`;
      });

      return [hourLabel, ...cellValues];
    });

    const data = [headers, ...rows];
    const workbook = xlsx.utils.book_new();
    const worksheet = xlsx.utils.aoa_to_sheet(data);
    xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    xlsx.writeFile(
      workbook,
      `${checkTodayDateWithFormat("YYYYMMDD")}_Hour_Optimization.xlsx`
    );
  };

  // -------- Render --------
  const selectedInView = filteredRows.filter((row) => isHourSelected(row.value)).length;

  // -------- Table columns (shared listing table config) --------
  const columns = [
    // Select checkbox column
    {
      id: "select",
      headerAlign: "center",
      align: "center",
      header: (
        <ListingCellCenter>
          <StyledWhiteCheckbox
            size="small"
            checked={areAllVisible()}
            onChange={(e) => toggleSelectAll(e.target.checked)}
          />
        </ListingCellCenter>
      ),
      render: (row) => {
        const unavailable = isRowUnavailable(row);
        const checkbox = (
          <StyledCheckbox
            size="small"
            checked={isHourSelected(row.value) && !unavailable}
            disabled={unavailable}
            onChange={() => toggleHour(row.value, row)}
          />
        );
        return (
          <ListingCellCenter>
            {unavailable ? (
              <HtmlTooltip title="No Store Open" arrow>
                <span>{checkbox}</span>
              </HtmlTooltip>
            ) : (
              checkbox
            )}
          </ListingCellCenter>
        );
      },
    },

    // Hour of Day label
    {
      id: "hour",
      header: <ListingHeaderContent>Hour of Day</ListingHeaderContent>,
      render: (row) => <ListingCellLeft>{row.label}</ListingCellLeft>,
    },

    // One column per selected weekday
    ...sortedWeekdayColumns.map((day) => ({
      id: `day_${day.value}`,
      header: <ListingHeaderContent>{day.label}</ListingHeaderContent>,
      headerSx: WEEK_COUNT_COL_SX,
      cellSx: WEEK_COUNT_COL_SX,
      render: (row, rowIdx) => {
        const cell = resolveCell(hourMatrix[row.value], day.value, storeTotal);
        const tone = availabilityTone(cell.count, cell.total);
        const suffix = rowIdx === 0 ? " Stores" : "";
        return (
          <ListingCellLeft sx={{ color: AVAILABILITY_TONES[tone] }}>
            {`${cell.count}/${cell.total}${suffix}`}
          </ListingCellLeft>
        );
      },
    })),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6">
          {usedFor === "update" ? "Edit" : "Add"} Contact-based Visual: Detailed Hour Selection
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select the hours of the day during which the visual should be played.
          The cells display the number of selected stores that are open during each hour for the corresponding weekday.
        </Typography>

        {/* -------- Store Availability filter -------- */}
        <Box sx={{ mb: 2 }}>
          <SingleSelect
            label="Store Availability"
            usedFor="filter"
            allowClearance={false}
            disableSearch={true}
            options={STORE_AVAILABILITY_OPTIONS}
            defaultVal={availabilityFilter}
            selectionCheck={(option, value) => option.value === value.value}
            onChangeCall={setAvailabilityFilter}
          />
        </Box>

        <StoreAvailabilityLegend />

        {/* -------- Table -------- */}
        <ListingDataTableForModal
          columns={columns}
          rows={filteredRows}
          rowKey={(row) => row.value}
          emptyText="No hours available"
          fitContent
        />

        {/* -------- Footer: counter + download -------- */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <StyledDarkRedInformationTypography variant="body2">
            Selected {selectedInView} of {filteredRows.length}
          </StyledDarkRedInformationTypography>

          {filteredRows.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={-1}>
              <TextButton
                onClick={(e) => {
                  e.preventDefault();
                  downloadExcelFile();
                }}
                label="Download Excel"
                icon={<Download />}
              />
              <HtmlTooltip
                title={
                  <Box sx={{ maxWidth: 200 }}>
                    <Typography variant="body2">
                      The export reflects the current state of data at the time of download.
                      Data in CAP Explorer may change over time.
                    </Typography>
                  </Box>
                }
              >
                <IconContainer>
                  <Info fontSize="small" sx={{ marginTop: 1 }} />
                </IconContainer>
              </HtmlTooltip>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <CloseButton onClick={onClose} label="Cancel" icon={<Close />} />
          <ActionButton
            onClick={handleApply}
            label="Apply"
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DetailedHourSelection;
